/* eslint-disable lingui/text-restrictions */

import { i18n } from "@lingui/core";
import { msg } from "@lingui/macro";

import { isFeatureEnabled } from "@/client/constants/feature-flags";
import { DEFAULT_MAX_TOKENS, DEFAULT_MODEL } from "@/client/constants/llm";
import { axios } from "@/client/libs/axios";
import { useOpenAiStore } from "@/client/stores/openai";
import { useResumeStore } from "@/client/stores/resume";

import { aiCache } from "./cache-manager";
import { openai } from "./client";
import { getOptimalModel } from "./model-selector";
import { optimizePrompt } from "./prompt-optimizer";

export type Mood =
  | "professional"
  | "casual"
  | "confident"
  | "friendly"
  | "encouraging"
  | "humorous"
  | "formal";

const getPrompt = (language = "en") => {
  const languageName = getLanguageName(language);
  return `You're an expert writer, can you help me to make my text sound more {mood}, while maintaining the meaning? Don't add any information that isn't in the original text. Please respond in ${languageName}. Here's the text:

"""
{input}
"""`;
};

const getLanguageName = (locale: string): string => {
  const languageMap: Record<string, string> = {
    ar: "Arabic",
    en: "English",
    fr: "French",
    es: "Spanish",
    de: "German",
    it: "Italian",
    pt: "Portuguese",
    ru: "Russian",
    ja: "Japanese",
    ko: "Korean",
    zh: "Chinese",
    hi: "Hindi",
    nl: "Dutch",
    sv: "Swedish",
    da: "Danish",
    no: "Norwegian",
    fi: "Finnish",
    pl: "Polish",
    tr: "Turkish",
  };

  const langCode = locale?.split("-")[0] || "en";
  return languageMap[langCode] || "English";
};

export const changeTone = async (text: string, mood: Mood, language?: string) => {
  // Get language from resume store if not provided
  if (!language) {
    try {
      const resumeState = useResumeStore.getState();
      language = resumeState.resume.data.metadata.locale || "en-US";
    } catch {
      language = "en-US";
    }
  }
  const { model, maxTokens, useSystemKey } = useOpenAiStore.getState();

  // If using system API, send request to our backend AI gateway
  if (useSystemKey) {
    try {
      const response = await axios.post("/ai/write/tone", {
        text,
        tone: mood,
        language,
      });
      // Return the text directly as that's what the backend sends
      return response.data || text;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : i18n._(msg`Unknown error`);
      throw new Error(i18n._(msg`Failed to change text tone: ${errorMessage}`));
    }
  }

  // Otherwise use OpenAI client directly
  const PROMPT = getPrompt(language);
  let prompt = PROMPT.replace("{mood}", mood).replace("{input}", text);

  // Get optimal model if feature flag is enabled
  const useOptimizedModels = isFeatureEnabled("OPTIMIZED_AI_MODELS");
  const modelConfig = useOptimizedModels
    ? getOptimalModel("tone")
    : {
        model: model ?? DEFAULT_MODEL,
        maxTokens: maxTokens ?? DEFAULT_MAX_TOKENS,
        temperature: 0.5,
      };

  // Check cache first (include mood in cache key)
  const cacheKey = `${mood}:${prompt}`;
  const cachedResponse = aiCache.get(cacheKey, modelConfig.model, "tone");
  if (cachedResponse) {
    return cachedResponse;
  }

  // Optimize prompt to reduce token usage
  const optimizedPrompt = optimizePrompt(prompt, "tone");
  prompt = optimizedPrompt.optimized;

  const client = await openai();
  const result = await client.chat.completions.create({
    messages: [{ role: "user", content: prompt }],
    model: modelConfig.model,
    max_tokens: modelConfig.maxTokens,
    temperature: modelConfig.temperature,
    stop: ['"""'],
    n: 1,
  });

  if (result.choices.length === 0) {
    throw new Error(i18n._(msg`OpenAI did not return any choices for your text.`));
  }

  const response = result.choices[0].message.content ?? text;

  // Cache the response for future use
  const tokensUsed = result.usage?.total_tokens ?? Math.ceil(prompt.length / 4);
  aiCache.set(cacheKey, response, modelConfig.model, tokensUsed, "tone");

  return response;
};
